import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { Tile } from "@/components/common/Tile";
import { Loading } from "@/components/common/Loading";
import {
  useSchedule, useCreateSlot, useDeleteSlot,
} from "@/hooks/useSchedule";
import { ScheduleSlot } from "@/api/schedule";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const ROW_MIN = 30;
const ROWS = 48;
const ROW_PX = 22;
const HEADER_PX = 28;
const DEFAULT_DURATION_MIN = 90;

const COLOR_CYCLE = [
  "bg-info-bg text-info-text border-info-text/40",
  "bg-success-bg text-success-text border-success-text/40",
  "bg-purple-bg text-purple-text border-purple-text/40",
  "bg-warning-bg text-warning-text border-warning-text/40",
  "bg-danger-bg text-danger-text border-danger-text/40",
];

function fmtTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function overlaps(a: ScheduleSlot, day: number, startMin: number, durMin: number): boolean {
  if (a.day_of_week !== day) return false;
  return startMin < a.start_minute + a.duration_min && startMin + durMin > a.start_minute;
}

// JS Date.getDay(): 0=Sun .. 6=Sat. Schedule uses Mon=0..Sun=6.
function jsDayToScheduleDay(jsDay: number): number {
  return (jsDay + 6) % 7;
}

export default function DeviceSchedule() {
  const { deviceId = "" } = useParams<{ deviceId: string }>();
  const { data, isLoading } = useSchedule(deviceId);
  const createSlot = useCreateSlot(deviceId);
  const deleteSlot = useDeleteSlot(deviceId);

  // Tick the "now" line each minute so it stays accurate without page churn.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const slots = data?.slots ?? [];

  const slotsByDay = useMemo(() => {
    const m: Record<number, ScheduleSlot[]> = {};
    for (const s of slots) (m[s.day_of_week] ??= []).push(s);
    return m;
  }, [slots]);

  function clickCell(day: number, row: number) {
    const startMin = row * ROW_MIN;
    const dur = DEFAULT_DURATION_MIN;
    if (startMin + dur > ROWS * ROW_MIN) return;
    if (slots.some((s) => overlaps(s, day, startMin, dur))) return;
    createSlot.mutate({
      day_of_week: day,
      start_minute: startMin,
      duration_min: dur,
      label: "Routine Test",
    });
  }

  if (isLoading) return <Loading />;

  const todayCol = jsDayToScheduleDay(now.getDay());
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const nowTop = HEADER_PX + (nowMin / ROW_MIN) * ROW_PX;

  return (
    <div className="flex flex-col gap-md">
      <div>
        <h1 className="text-2xl font-semibold text-text leading-tight">Schedule</h1>
        <p className="text-sm text-textLight">
          Tap an empty cell to add a {DEFAULT_DURATION_MIN}-minute test slot.
          Click an existing slot to remove it. ≥1 hour cool-down between tests
          is recommended.
        </p>
      </div>

      <Tile title="Weekly Schedule">
        <div className="overflow-auto max-h-[70vh]">
          <div className="relative min-w-[640px]">
            <div className="grid grid-cols-[60px_repeat(7,1fr)] gap-px bg-border/50 rounded-md overflow-hidden text-xs">
              <div
                className="bg-surface px-xs text-textLight font-medium flex items-center justify-end pr-xs"
                style={{ height: HEADER_PX }}
              >
                Time
              </div>
              {DAYS.map((d, idx) => (
                <div
                  key={d}
                  className={`bg-surface px-xs font-medium text-center flex items-center justify-center ${
                    idx === todayCol ? "text-brand" : "text-text"
                  }`}
                  style={{ height: HEADER_PX }}
                >
                  {d}{idx === todayCol ? " · today" : ""}
                </div>
              ))}

              {Array.from({ length: ROWS }).map((_, row) => (
                <React.Fragment key={row}>
                  <div
                    className="bg-surface px-xs text-textLight flex items-start justify-end pr-xs"
                    style={{ height: ROW_PX }}
                  >
                    {row % 2 === 0 ? fmtTime(row * ROW_MIN) : ""}
                  </div>
                  {DAYS.map((_, day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => clickCell(day, row)}
                      className="bg-surface hover:bg-brand/5 transition-colors text-left cursor-pointer"
                      style={{ height: ROW_PX }}
                      aria-label={`Create slot ${DAYS[day]} ${fmtTime(row * ROW_MIN)}`}
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>

            {/* Now-line overlay: spans the 7 day columns at the current minute */}
            <div
              className="absolute pointer-events-none flex items-center"
              style={{
                left: 60,
                right: 0,
                top: nowTop - 1,
                height: 2,
              }}
              aria-hidden="true"
            >
              <span className="block h-2 w-2 -ml-1 rounded-full bg-danger-text" />
              <span className="flex-1 h-[2px] bg-danger-text/80" />
              <span className="ml-xs text-[10px] font-medium text-danger-text bg-surface px-xs rounded">
                now · {fmtTime(nowMin)}
              </span>
            </div>

            <div
              className="grid grid-cols-[60px_repeat(7,1fr)] gap-px absolute inset-0 pointer-events-none"
              style={{ paddingTop: HEADER_PX }}
            >
              <div />
              {DAYS.map((_, day) => (
                <div key={day} className="relative">
                  {(slotsByDay[day] ?? []).map((s, idx) => {
                    const startRow = s.start_minute / ROW_MIN;
                    const spanRows = s.duration_min / ROW_MIN;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => deleteSlot.mutate(s.id)}
                        className={`group pointer-events-auto absolute left-1 right-1 rounded-md border ${COLOR_CYCLE[idx % COLOR_CYCLE.length]} px-xs py-xs text-left shadow-card hover:opacity-80 cursor-pointer ${
                          s.is_paused ? "opacity-50" : ""
                        }`}
                        style={{
                          top: startRow * ROW_PX + 1,
                          height: spanRows * ROW_PX - 2,
                        }}
                        title="Click to remove this test"
                        aria-label={`Delete ${s.label} ${fmtTime(s.start_minute)}`}
                      >
                        <div className="font-medium truncate text-xs flex items-center gap-xs">
                          <span className="flex-1 truncate">{s.label}</span>
                          <Trash2
                            size={11}
                            className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          />
                        </div>
                        <div className="opacity-70 text-xs">
                          {fmtTime(s.start_minute)}–{fmtTime(s.start_minute + s.duration_min)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Tile>
    </div>
  );
}
